import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { easing, fluid, reducedMotion } from '../styles/motion'; // [MOTION-A 2026-08-20] [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidDialog, fluidScrim, fluidCascade } from './Fluid'; // [FLUID-3 2026-10-12]
// [LIQUID-GLASS 2026-11-02, user] the window, deal cards, boxes and buttons use the shared liquid glass recipe
import {
  lgSurface, lgCard, lgSquircle, lgHair, lgGreen, lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { Payment } from '../types';
import {
  fetchHubSpotOwners,
  fetchHubSpotDeals,
  createHubSpotDeals,
  setPaymentRowChoice, // [AMAAN-ROW 2026-09-25]
  HubSpotOwner,
  HubSpotDeal,
  ExistingHubSpotDeal,
} from '../services/api';
import GlassSelect from './GlassSelect';

/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// [DATE-STYLE 2026-09-29, user] "9/29/2026" → "Sep 29, 2026" (one date style across the app).
const MONTH_SHORT_DAY_YEAR_UTC_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

import { toastError } from './toastService'; // [DEAL-SPLIT 2026-06-29] top-middle popup on split-sum mismatch
import { effectiveCurrentMonth } from '../utils/effectiveMonth'; // [AMAAN-ROW 2026-09-25]
import { assignPriorDeals, DealCard as DealSlotCard } from '../utils/dealSlots'; // [DEAL-REUSE 2026-11-03] one rule with the server

/* ───── styled components ───── */

// [AUTO-REFRESH 2026-08-03] See reloadGuard — blocks the auto-reload while this modal is open.
// [MOTION-A 2026-08-20] The parent keeps us mounted via usePresence while the close plays.
// [FLUID-3 2026-10-12, user] `$open` drives the fluid recipe (components/Fluid.tsx): the shade fades in,
// fades out quicker, and stops taking clicks the moment a close begins. Was a dcFadeIn / dcFadeOut pair.
const Overlay = styled.div.attrs<{ 'data-reload-block'?: string; $open: boolean }>({ 'data-reload-block': '' })`
  position: fixed;
  inset: 0;
  /* [LIQUID-GLASS 2026-11-02, user] The shade behind the window: a little darker, a softer blur, almost no colour boost. */
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(20, 30, 25, 0.14)'};
  backdrop-filter: blur(12px) saturate(115%);
  -webkit-backdrop-filter: blur(12px) saturate(115%);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  ${(p) => fluidScrim(p.$open)}

  /* [DEAL-SPLIT-TYPED 2026-10-30] Phones: the window takes the whole width, so the deal cards have room. */
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

// [FLUID-3 2026-10-12] The Split Deals width glide below has to share ONE transition list with the window's
// open / close (a second transition line replaces the first, it does not add to it), so fluidDialog's list is
// written out again here from the same tokens, with the width added.
const WIDTH_GLIDE = 'width 180ms cubic-bezier(0.16, 1, 0.3, 1)';

/* [LIQUID-GLASS 2026-11-02, user] The frosted glass of Edit Mapping / Add Payment (styles/liquidGlass.ts): softer blur,
   smooth 24 px corners, light top edge, soft bottom reflection. The window no longer scrolls: Body does (flex column
   below), so the rim never scrolls away and the blur never sits on a scrolling box. The old bottom shade is gone. */
const Modal = styled.div<{ $wide?: boolean; $open: boolean }>`
  position: relative;
  ${lgSurface(38)}
  ${lgSquircle(24)}
  display: flex;
  flex-direction: column;
  /* [DEAL-CONFIRM 2026-06-23] Was a very wide (1600px) deal TABLE. The confirm
     popup is a stacked card layout instead, so it reads as a dialog at a calm
     width and still never exceeds the Overlay's padded area on small screens.
     [DEAL-SPLIT 2026-06-29] When Split Deals is on, each deal becomes a side-by-side
     PAIR of cards, so the dialog widens to fit two columns (still capped to the
     viewport's padded area, and it collapses to one column on narrow screens). */
  width: ${(p) => (p.$wide ? 'min(940px, calc(100vw / var(--app-zoom, 1) - 48px))' : 'min(580px, calc(100vw / var(--app-zoom, 1) - 48px))')}; /* [APP-FIT 2026-10-26] the window in page px */
  max-height: calc(90vh / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  overflow: hidden;
  /* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring (readable after ~0.1 s, open by
     0.36 s), closes in 0.18 s; the header, the deal cards and the buttons rise in one after another (and again
     when the ROW or US question takes the window over). Replaces the dcPopIn / dcPopOut keyframes. The
     transition lines after fluidDialog are its own list plus the width glide (see WIDTH_GLIDE): plain curve
     first, the spring line after it for browsers that know linear(). */
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open
    ? `transition: transform ${fluid.dialogIn} ${easing.emphasized}, opacity ${fluid.fade} ${easing.entrance}, ${WIDTH_GLIDE};
  transition: transform ${fluid.dialogIn} ${fluid.spring}, opacity ${fluid.fade} ${easing.entrance}, ${WIDTH_GLIDE};`
    : `transition: transform ${fluid.dialogOut} ${easing.exit}, opacity ${fluid.dialogOut} ${easing.standard}, ${WIDTH_GLIDE};`)}
  ${reducedMotion} {
    transition: none;
  }
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 3) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  /* [DEAL-SPLIT-TYPED 2026-10-30] Phones: the whole screen less the shade's 8 px on each side. */
  @media (max-width: 768px) {
    width: ${(p) => (p.$wide ? 'min(940px, calc(100vw / var(--app-zoom, 1) - 16px))' : 'min(580px, calc(100vw / var(--app-zoom, 1) - 16px))')};
    max-height: calc(100dvh / var(--app-zoom, 1) - 16px);
  }

  & > * { position: relative; z-index: 1; }
`;

const Header = styled.div`
  padding: 18px 24px;
  flex-shrink: 0; /* [LIQUID-GLASS 2026-11-02] stays put; Body scrolls */
  background: transparent;
  border-bottom: 1px solid ${lgHair};
  border-radius: 16px 16px 0 0;

  @media (max-width: 768px) { padding: 14px; } /* [DEAL-SPLIT-TYPED 2026-10-30] */
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.green600};
  text-shadow: ${(p) => p.theme.colorScheme === 'dark' ? '0 1px 2px rgba(0,0,0,0.45)' : 'none'};
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 8px;

  /* [LIQUID-GLASS 2026-11-02] A small glossy green capsule, like Edit Mapping's. */
  &::before {
    content: '';
    width: 4px;
    height: 18px;
    background: ${(p) => p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, #8ff7c6, #4acd8b)'
      : 'linear-gradient(180deg, #34d39e, #1a7f64)'};
    border-radius: 999px;
    box-shadow: 0 0 10px color-mix(in srgb, ${lgGreen} 40%, transparent);
  }
`;

const Body = styled.div`
  padding: 18px 24px 4px;
  /* [LIQUID-GLASS 2026-11-02] the part that scrolls when the window is taller than the screen */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  @media (max-width: 768px) { padding: 14px 12px 4px; } /* [DEAL-SPLIT-TYPED 2026-10-30] */
`;

/* [DEAL-CONFIRM 2026-06-23] "✓ Data saved successfully" — this popup replaces the
   old generic "Data Saved Successfully!" confirmation, so it carries the save
   confirmation itself (the mapping is already saved by the time it opens). */
const SavedNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600};

  &::before {
    content: '✓';
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    font-size: 11px;
    color: #fff;
    background: ${(p) => p.theme.colorScheme === 'dark' ? '#44b57b' : p.theme.green600};
  }
`;

const Subhead = styled.p`
  margin: 8px 0 14px;
  font-size: 13px;
  line-height: 1.45;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.72)' : 'rgba(22,25,23,0.65)'};
`;

const NoticeMsg = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#fbbf24' : '#b45309'};
  padding: 0 0 12px;
`;

// [LIQUID-GLASS 2026-11-02, user] A glass card on the window: see-through, light top edge, smooth 18 px corners.
const DealCard = styled.div`
  position: relative;
  ${lgCard}
  ${lgSquircle(18)}
  padding: 14px 16px;
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }

  @media (max-width: 768px) { padding: 12px; } /* [DEAL-SPLIT-TYPED 2026-10-30] */
`;

const DealCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const DealName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917'};
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

/* [PIA-DEAL 2026-09-09] The "(PIA)" that follows the website on a PIA card, so the PIA deal is
   tellable at a glance from the sale/upsell cards stacked above it. Lighter than the domain on
   purpose — it labels the card, it is not part of the website. DISPLAY ONLY: the deal name sent
   to HubSpot stays the bare website, because renamePaymentDeals rewrites a deal's name to the
   website whenever the website is corrected, which would silently strip any suffix we added. */
const PiaTag = styled.span`
  font-weight: 600;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.55)')};
`;

const ModeBadge = styled.span<{ $update?: boolean }>`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 9px;
  border-radius: 999px;
  ${(p) => {
    const dark = p.theme.colorScheme === 'dark';
    if (p.$update) {
      // HubSpot orange family — "this updates an existing deal".
      return dark
        ? `color: #ffb39e; background: rgba(255,122,89,0.16); border: 1px solid rgba(255,122,89,0.40);`
        : `color: #c2410c; background: rgba(255,122,89,0.13); border: 1px solid rgba(255,122,89,0.36);`;
    }
    return dark
      ? `color: #7de5ab; background: rgba(68,181,123,0.16); border: 1px solid rgba(68,181,123,0.40);`
      : `color: #1e765e; background: rgba(19,183,144,0.13); border: 1px solid rgba(19,183,144,0.36);`;
  }}
  /* [LIQUID-GLASS 2026-11-02] a glossy capsule: soft shine on top and a light top edge */
  background-image: linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 104px 1fr;
  align-items: center;
  gap: 10px;
  padding: 5px 0;

  /* [DEAL-SPLIT-TYPED 2026-10-30] Phones: a narrower label column, so the field gets the room. */
  @media (max-width: 768px) {
    grid-template-columns: 92px minmax(0, 1fr);
    gap: 8px;
  }
`;

const FieldLabel = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.muted};
`;

const FieldValue = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.92)' : '#161917'};
  overflow-wrap: anywhere;
  min-width: 0;
`;

const OwnerWrap = styled.div`
  flex: 0 1 260px;
  min-width: 160px;

  /* [DEAL-SPLIT-TYPED 2026-10-30] Phones: fills its row. The 160 px floor pushed it out of the card on a 360 px screen. */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 0;
  }
`;

/* Match the input look used on the Mapping modal.
   [LIQUID-GLASS 2026-11-02, user] Now Edit Mapping's pressed-in glass box (10 px corners, soft green ring + glow while
   open); its list opens as frosted glass (liquidList on each element). */
const SUNK_DARK = 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)';
const SUNK_LIGHT = 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)';
const Select = styled(GlassSelect)`
  && {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    height: 32px;
    padding: 0 28px 0 10px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    justify-content: flex-start;
    border: 1px solid transparent;
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)'};
    background-image: none;
    box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? SUNK_DARK : SUNK_LIGHT)};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
    transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  &&:hover:not(:disabled) {
    border-color: transparent;
  }

  &&[aria-expanded="true"],
  &&:focus,
  &&:focus-visible {
    outline: none;
    border-color: color-mix(in srgb, ${lgGreen} 70%, transparent);
    box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? SUNK_DARK : SUNK_LIGHT)},
      0 0 0 4px color-mix(in srgb, ${lgGreen} 16%, transparent),
      0 0 18px color-mix(in srgb, ${lgGreen} 24%, transparent);
  }

  /* [DEAL-SPLIT-TYPED 2026-10-30] Phones: as tall as the typed boxes, easy to tap. */
  @media (max-width: 768px) {
    && { height: 38px; }
  }
`;

/* [DEAL-CONFIRM 2026-06-23] The inline "old value" annotation on an update. Shows
   only when the field actually changed, so an update with one tweaked field reads
   "this deal WAS in X — now it's Y" at a glance. Uses the HubSpot-orange accent so
   the diff stands apart from the (green) save chrome. */
const WasNote = styled.span<{ $emphasis?: boolean }>`
  font-size: 11.5px;
  font-weight: 600;
  font-style: italic;
  white-space: nowrap;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffb39e' : '#c2410c'};
  ${(p) => p.$emphasis ? `
    font-style: normal;
    padding: 1px 7px;
    border-radius: 999px;
    background: ${p.theme.colorScheme === 'dark' ? 'rgba(255,122,89,0.16)' : 'rgba(255,122,89,0.12)'};
  ` : ''}
`;

/* [SECOND-VOICE-MULTI 2026-08-06] A multi-select trigger collapses to "N voices" —
   GlassSelect never lets it grow with the selection, or the card reflows. So when more
   than one name is picked the full list is spelled out beside it: this is verbatim the
   string that will be written to HubSpot. A single pick already reads in full on the
   trigger itself, so this stays hidden for the common case. */
const SVNames = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  overflow-wrap: anywhere;
  min-width: 0;
`;

const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin-top: 11px;
  padding-top: 10px;
  border-top: 1px dashed ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  font-size: 11px;
  color: ${(p) => p.theme.muted};
`;

const MetaItem = styled.span`
  overflow-wrap: anywhere;
  b { font-weight: 700; color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(22,25,23,0.7)'}; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  flex-shrink: 0; /* [LIQUID-GLASS 2026-11-02] stays put; Body scrolls */
  border-top: 1px solid ${lgHair};
  background: transparent;
  border-radius: 0 0 16px 16px;

  @media (max-width: 768px) { padding: 12px 14px; } /* [DEAL-SPLIT-TYPED 2026-10-30] */
`;

// [BUTTON-STYLE 2026-09-30] 'primary' = the green primary, otherwise the secondary; min-width stays (layout).
// [LIQUID-GLASS 2026-11-02] Glass capsules (squish + press light).
const Btn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $variant?: 'primary' }>`
  ${(p) => (p.$variant === 'primary' ? lgButtonPrimary : lgButtonSecondary)}
  min-width: 96px;

  /* [DEAL-SPLIT-TYPED 2026-10-30] Phones: the buttons share the row and are easy to tap, as in ReportPeopleDialog. */
  @media (max-width: 768px) {
    flex: 1 1 0;
    height: 40px;
  }
`;

/* [AMAAN-ROW 2026-09-25] The one explanatory line under the "ROW side or US side?" question. */
const RowAskNote = styled.p`
  margin: 10px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${(p) => p.theme.dim};
`;

const ErrorMsg = styled.div`
  color: #dc2626;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 0;
`;

const RetryLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 6px;
  color: #2563eb;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
`;

/* ───── [DEAL-SPLIT 2026-06-29] split-deal chrome ───── */

// The header is now a row: title on the left, the Split Deals control on the right.
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

// "Split Deals" label + the toggle switch, sitting at the right of the header.
const SplitControl = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
`;

const SplitLabel = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.82)' : 'rgba(22,25,23,0.78)'};
`;

// A pill toggle. The native checkbox is visually hidden but still drives state +
// accessibility; the knob slides and the track turns green when on.
const Toggle = styled.span<{ $on: boolean }>`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  border-radius: 999px;
  flex-shrink: 0;
  transition: background 180ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(p) => p.$on
    ? (p.theme.colorScheme === 'dark'
        ? 'linear-gradient(180deg, rgba(70,191,130,0.85) 0%, rgba(61,158,108,0.95) 100%)'
        : 'linear-gradient(180deg, rgba(13,193,152,0.95) 0%, rgba(26,159,125,1) 100%)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(22,25,23,0.18)')};
  /* [LIQUID-GLASS 2026-11-02] the track is pressed in; the knob is a glossy pearl */
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.25), inset 0 -1px 0 rgba(255,255,255,0.10);

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: linear-gradient(180deg, #ffffff, #eef1f0);
    box-shadow: 0 1px 3px rgba(0,0,0,0.30), inset 0 -1px 0 rgba(0,0,0,0.06);
    transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
    transform: translateX(${(p) => (p.$on ? '18px' : '0')});
  }

  input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
  }
`;

// Two split cards side by side. Collapses to one column on a narrow viewport.
const SplitPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }

  /* The cards inside already carry their own bottom margin; cancel it here so the
     grid gap is the only spacing between the pair. */
  & > div { margin-bottom: 0; }
`;

/* [DEAL-SPLIT-TYPED 2026-10-30, user] "I don't want this slider option and I want the value enter box", then "no share
   percent thing, only want the amount thing": the Amount of a split side is a typed box, and that is the whole control.
   Replaces the 10% slider and the Share % of [DEAL-SPLIT-PERCENT 2026-07-22]. The flat look and the size are the Name
   dropdown's (Select), so the rows line up. The label carries the look, so the "$" sits inside the box and a press on it
   still lands in the input. */
const SplitBox = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
  height: 32px;
  padding: 0 10px;
  /* [LIQUID-GLASS 2026-11-02] the pressed-in glass box, like the Name dropdown beside it */
  border-radius: 10px;
  border: 1px solid transparent;
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)'};
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? SUNK_DARK : SUNK_LIGHT)};
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  cursor: text;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:focus-within {
    border-color: color-mix(in srgb, ${lgGreen} 70%, transparent);
    box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? SUNK_DARK : SUNK_LIGHT)},
      0 0 0 4px color-mix(in srgb, ${lgGreen} 16%, transparent),
      0 0 18px color-mix(in srgb, ${lgGreen} 24%, transparent);
  }

  input {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    font: inherit;
  }
  input:disabled { opacity: 0.6; }
  span {
    flex: none;
    color: ${(p) => p.theme.muted};
  }

  /* Phones: taller to tap, and 16 px text so the phone does not zoom the page in on focus. */
  @media (max-width: 768px) {
    height: 38px;
    input { font-size: 16px; }
  }
`;

// The typed Amount of a split side. While the box has the focus it keeps just what is being typed ("250." stays "250.");
// at any other time it shows `shown`, the side's real amount. A press selects the old value, so typing replaces it. Digits
// and one dot, at most 2 decimals, as in SplitInventoryModal; a pasted "$1,000.50" comes through clean.
const SplitAmountBox: React.FC<{
  label: string;
  shown: string;
  disabled: boolean;
  onType: (value: number) => void;
}> = ({ label, shown, disabled, onType }) => {
  const [typing, setTyping] = useState<string | null>(null);
  return (
    <SplitBox>
      <span aria-hidden="true">$</span>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={label}
        disabled={disabled}
        value={typing ?? shown}
        onFocus={(e) => { setTyping(shown); e.target.select(); }}
        onBlur={() => setTyping(null)}
        onChange={(e) => {
          let raw = e.target.value.replace(/[^0-9.]/g, '');
          const dot = raw.indexOf('.');
          if (dot !== -1) raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '').slice(0, 2);
          setTyping(raw);
          onType(Number(raw) || 0);
        }}
      />
    </SplitBox>
  );
};

// Live "must total $X" line under a split pair — green when the two amounts add up,
// amber/red when they don't (mirrors the top-middle popup shown on submit).
const SplitHint = styled.div<{ $ok: boolean }>`
  margin: -2px 0 14px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: ${(p) => p.$ok
    ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)
    : (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')};
`;

/* ───── constants ───── */

// [DEAL-SPLIT 2026-06-29] One half of a deal slot. In normal mode a slot has a
// single part (part 0); toggling Split Deals adds part 1, and the two parts' amounts
// must add back up to the slot's mapped total. `existingDealId` present ⇒ this part
// UPDATES that HubSpot deal; absent ⇒ it CREATES a new one (so a first-time split is
// "one updates, one creates"). `amountStr` is the editable dollar string for the part.
interface SplitPart {
  ownerName: string;
  amountStr: string;
  existingDealId?: string;
  // [SECOND-VOICE 2026-08-04] The second sales rep credited on this deal, or the
  // literal 'Self Closed' when the Deal Owner closed it alone. SALES deals only
  // (Sales Pipeline = r_new_sale / o_new_sale); PM upsell deals never carry it.
  // Per PART, not per slot — each split half is its own HubSpot deal with its own
  // owner, so it gets its own second voice.
  secondVoice: string;
}

// One deal-type slot (Recurring/One-Time New Sale or Upsell) built from the mapping.
// Carries the ORIGINAL department breakdown + total (the split basis) and its 1–2
// parts. In split mode the part amounts are edited and the four departments are
// divided PROPORTIONALLY between the two deals (preserving each department's total).
interface SlotRow {
  suffix: string;
  dealName: string;
  pipeline: string;
  paymentType: string;
  closeDate: string;
  companyUrl: string;
  email: string;
  ranking: number;
  performance: number;
  tech: number;
  creative: number;
  originalTotal: number;
  parts: [SplitPart, SplitPart];
  preSplit: boolean;
  /* [PIA-DEAL 2026-09-09] The `PIA Month` string for a PIA slot ("5 (till December 2026)"),
     '' on every other slot. Only PIA slots ever send it. */
  piaMonth: string;
}

// [SECOND-VOICE 2026-08-05] `sv` = the Second Voice this deal was last saved with, kept
// here so the popup pre-fills it with no round trip (see the server-side note).
interface StoredSplitPart { dealId?: string; owner?: string; amount?: number; sv?: string; }

/* ───── component ───── */

interface Props {
  payment: Payment;
  savedFields: Record<string, any>;
  // dealOwner = the owner name(s) the deal(s) were created on, echoed back from the
  // server so the parent can reflect it on the row (and the Edit Mapping popup).
  // dealSplits = the JSON split layout the server persisted (or null when no split),
  // so the parent can stamp it on the row and a re-open re-uses the same deal ids.
  // [AMAAN-ROW 2026-09-25] rowStatus = the payment's row_status after the "ROW side or US side?"
  // answer ('ROW' or null) — passed ONLY when that question was asked, so the parent can show the
  // ROW tag on the row at once. Undefined = not asked; leave the row's flag as it is.
  onClose: (updatedDealIds?: string, dealOwner?: string, dealSplits?: string | null, rowStatus?: 'ROW' | null) => void;
  // [MOTION-A 2026-08-20] True while the parent holds us mounted for the exit animation.
  closing?: boolean;
}

function fmt(n: number) {
  return `$${MONEY_FMT.format(n)}`;
}

function fmtDate(s: string) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return MONTH_SHORT_DAY_YEAR_UTC_FMT.format(d);
}

// Dollars → integer cents (round to kill float noise). Used for all split math so
// "$400 + $250 = $650" is an exact integer comparison, never a 649.9999 miss.
function toCents(n: number | string): number {
  return Math.round((Number(n) || 0) * 100);
}

// A clean editable dollar string: whole numbers show no decimals ("650"), otherwise
// up to 2 dp ("399.5", "333.33").
function fmtAmountStr(n: number): string {
  if (!isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
}

// Parse the (raw) deal_splits JSON off a payment into a slot→parts map. Defensive:
// accepts a JSON string (the DB form) or an already-parsed object, and never throws.
function parseDealSplits(raw: unknown): Record<string, StoredSplitPart[]> {
  if (!raw) return {};
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return obj && typeof obj === 'object' ? (obj as Record<string, StoredSplitPart[]>) : {};
  } catch {
    return {};
  }
}

// Split the four department cents PROPORTIONALLY so part A totals exactly
// `amountACents` and part B gets the remainder — guaranteeing (a) each department's
// A+B equals the original (HubSpot dept reporting is untouched in aggregate) and
// (b) A's total is exactly what the user typed. Uses largest-remainder rounding so
// the proportional split lands on exact cents with no drift.
function proportionalSplitCents(origCents: number[], amountACents: number): [number[], number[]] {
  const total = origCents.reduce((a, b) => a + b, 0);
  if (total <= 0) return [origCents.map(() => 0), origCents.map(() => 0)];
  const clampedA = Math.max(0, Math.min(amountACents, total));
  const raw = origCents.map((c) => (c * clampedA) / total);
  const aCents = raw.map((r) => Math.floor(r));
  let leftover = clampedA - aCents.reduce((a, b) => a + b, 0);
  // Hand the leftover cents to the departments with the largest fractional part,
  // skipping any with no original value (a $0 dept must stay $0 in both halves).
  const order = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .filter((x) => origCents[x.i] > 0)
    .sort((a, b) => b.frac - a.frac);
  let guard = 0;
  while (leftover > 0 && order.length > 0 && guard < order.length * 4) {
    const idx = order[guard % order.length].i;
    if (aCents[idx] < origCents[idx]) { aCents[idx]++; leftover--; }
    guard++;
  }
  const bCents = origCents.map((c, i) => c - aCents[i]);
  return [aCents, bCents];
}

// The four deal types a mapping can produce, in the fixed order the server expects.
// [SECOND-VOICE 2026-08-04] Pinned first option — the Deal Owner closed this one alone.
// Written to HubSpot's `second_voice` verbatim so it is filterable there and clearly
// distinct from a blank (= an old deal nobody has filled in yet).
const SELF_CLOSED = 'Self Closed';

/* [SECOND-VOICE-MULTI 2026-08-06] A deal can credit MORE THAN ONE second voice.
   The value stays a single STRING end to end — HubSpot's `second_voice` is a plain
   text property, so several names are stored joined by ', ' ("Shivam Sharma, Anjani
   Mishra"). Left as text on purpose: HubSpot's native multi-select (enumeration +
   checkbox) stores its values SEMICOLON-separated, and needs a fixed options list
   that would have to be re-synced every time a rep joins or leaves. As plain text
   nothing on the portal changes, and the 51 deals already carrying a single name
   stay valid untouched — a one-name list is just that name. */
const SV_SEP = ', ';
const parseSV = (s: string): string[] =>
  String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const joinSV = (names: string[]): string => names.join(SV_SEP);
/* Re-join through the parser so ANY spacing HubSpot hands back ("A,B", "A ,B")
   compares equal to our canonical "A, B" — otherwise the prefill would rewrite the
   field and the "was" note would fire on a difference the user never made. */
const normSV = (s: string): string => joinSV(parseSV(s));

// Sales-side slots — the ONLY ones that carry a Second Voice. Kept in sync with
// DEAL_TYPES below by the pipeline check (`!isPM`); this list documents the intent.
const SALES_PIPELINE = 'Sales Pipeline';
const UPSELL_PIPELINE = 'Upsell - Project Managers';

/* ── [PIA-DEAL 2026-09-09] PIA ────────────────────────────────────────────────────────────────
   PIA is the fourth kind of money a payment can be mapped to, and until now the only one that
   never produced a deal: the mapping simply saved and HubSpot never heard about it.
   Three things make it different from the four slots above:

   1. ITS PIPELINE IS NOT FIXED. A sale is always Sales Pipeline and an upsell always the PM one,
      but a PIA is tagged PM or Sales by the user (`ranking_pia_dept`), and follows that tag. That
      is what makes the owner dropdown correct for free: renderCard picks pmOwnerNames vs
      salesOwnerNames off the row's PIPELINE, so routing by department IS the name split. (A
      single dedicated "PIA" pipeline would have broken exactly that.)
   2. IT NEVER SPLITS. A PIA belongs to one person, so the Split Deals toggle skips it.
   3. IT CARRIES `pia_month` — the term plus the month it runs through.

   It is LAST in DEAL_TYPES on purpose, and must stay last: routes/hubspot.ts relies on
   Promise.all order to line the created ids up with the flat positional list in
   `deals_record_id`, so inserting PIA earlier would re-attach older payments' existing deals to
   the wrong slots. Appending is also what puts its card below the others, which is the layout
   the feature was asked for. */
const PIA_SUFFIX = 'pia_amount';
const PIA_PAYMENT_TYPE = 'PIA';
/* Only the RANKING department can hold a PIA — the mapping screen locks the other three — so the
   department tag is read from that one column. */
const PIA_DEPT_FIELD = 'ranking_pia_dept';
const PIA_TERM_FIELD = 'ranking_pia_month';

/** A PIA tagged 'PM' becomes a PM deal; anything else is a Sales deal. */
const piaPipeline = (f: Record<string, any>): string =>
  String(f[PIA_DEPT_FIELD] ?? '').trim() === 'PM' ? UPSELL_PIPELINE : SALES_PIPELINE;

/** PIA slots never split, and always offer a Second Voice whichever pipeline their department
 *  tag sent them to. */
const isPiaRow = (r: SlotRow): boolean => r.suffix === PIA_SUFFIX;

type DealType = {
  suffix: string;
  /** A fixed pipeline, or one derived from the saved mapping (PIA reads its department tag). */
  pipeline: string | ((f: Record<string, any>) => string);
  paymentType: string;
};

const DEAL_TYPES: DealType[] = [
  { suffix: 'r_new_sale', pipeline: 'Sales Pipeline', paymentType: 'Recurring sale' },
  { suffix: 'o_new_sale', pipeline: 'Sales Pipeline', paymentType: 'Monthly - One Time' },
  { suffix: 'r_so', pipeline: 'Upsell - Project Managers', paymentType: 'Recurring sale' },
  { suffix: 'o_so', pipeline: 'Upsell - Project Managers', paymentType: 'Monthly - One Time' },
  { suffix: PIA_SUFFIX, pipeline: piaPipeline, paymentType: PIA_PAYMENT_TYPE }, // keep LAST
];

/** Full month + year in UTC — "December 2026". Deliberately the LONG month name: the mapping
 *  screen's own inline hint uses the short form, but the HubSpot field is read by people who are
 *  not looking at that hint. */
const PIA_MONTH_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' });

/**
 * The value written to HubSpot's `PIA Month` — the term, and the month it runs THROUGH:
 *     term 5 on a 2026-08 payment  ->  "5 (till December 2026)"
 *
 * The term counts the payment's own month, so the last covered month is month + (term - 1) and is
 * INCLUSIVE — the same arithmetic MappingModal's piaLandingMonth uses for "Covers this payment's
 * month through …". Returns '' when there is no usable month or term, and the caller then leaves
 * the property out of the payload entirely rather than writing a half-answer.
 */
function piaMonthLabel(monthStr: string | null | undefined, term: unknown): string {
  const n = parseInt(String(term ?? ''), 10);
  if (!monthStr || !Number.isFinite(n) || n < 1) return '';
  const [y, m] = String(monthStr).slice(0, 7).split('-').map(Number);
  if (!y || !m) return '';
  // Date.UTC rolls the year over by itself, so a long term on a December payment is fine.
  return `${n} (till ${PIA_MONTH_FMT.format(new Date(Date.UTC(y, m - 1 + (n - 1), 1)))})`;
}

const EMPTY_PART: SplitPart = { ownerName: '', amountStr: '', existingDealId: undefined, secondVoice: SELF_CLOSED };

// [DEAL-SPLIT-TYPED 2026-10-30, user] One side takes `sideC` cents and the other side the rest of the slot's mapped total,
// so the two add up exactly. A side typed over the total leaves the other at $0: the SplitHint says so and handleCreate
// refuses it.
function withSideCents(r: SlotRow, partIdx: number, sideC: number): SlotRow {
  const parts = r.parts.slice() as [SplitPart, SplitPart];
  parts[partIdx] = { ...parts[partIdx], amountStr: fmtAmountStr(sideC / 100) };
  parts[1 - partIdx] = { ...parts[1 - partIdx], amountStr: fmtAmountStr(Math.max(0, toCents(r.originalTotal) - sideC) / 100) };
  return { ...r, parts };
}

// [DEAL-SPLIT-TYPED 2026-10-30] What the box shows when it is not being typed in: the real amount (no ".00" on a whole number).
function partAmountText(r: SlotRow, partIdx: number): string {
  const c = toCents(r.parts[partIdx].amountStr);
  return c % 100 === 0 ? String(c / 100) : (c / 100).toFixed(2);
}

const DealCreationModal: React.FC<Props> = ({ payment, savedFields, onClose, closing = false }) => {
  /* [AMAAN-ROW 2026-09-25, user] Set after a successful create/update whose response says a
     both-books rep (Amaan Sofi) owns a New Sale deal on this current-month payment. While set, the
     dialog shows ONLY the "ROW side or US side?" question; the deal results wait here and are
     handed to onClose once the answer is saved. */
  // `afterSave` is set only on the FALLBACK path (the server asked after saving); normally the popup
  // opens the moment Create is pressed and the answer travels WITH the deal save — one request.
  const [rowAsk, setRowAsk] = useState<null | {
    current: 'ROW' | 'US';
    afterSave?: { dealIds: string; dealOwner?: string; dealSplits: string | null };
  }>(null);
  const [rowSaving, setRowSaving] = useState<'ROW' | 'US' | null>(null);
  const [owners, setOwners] = useState<HubSpotOwner[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  // [FLUID-3 2026-10-12] Open one frame after mount (so the spring has a closed state to start from) until the
  // parent starts the close.
  const open = usePresence(!closing, fluid.dialogOutMs).entered;

  // Owner name(s) the existing deal(s) were last created/updated on, parsed from
  // the stored (comma-joined, same order as deals_record_id) deal_owner. Used as
  // the immediate pre-fill / "was" value before the live HubSpot read lands.
  const storedOwners = useMemo(
    () => (payment.deal_owner || '').split(',').map((s) => s.trim()),
    [payment.deal_owner]
  );

  // [DEAL-SPLIT 2026-06-29] The stored split layout (slot → 1–2 deals), authoritative
  // for which HubSpot deal id / owner / amount belongs to each slot+part. Present once
  // a deal was created via this flow; absent ⇒ a pre-feature row (positional fallback).
  const storedSplits = useMemo(() => parseDealSplits(payment.deal_splits), [payment.deal_splits]);

  // [DEAL-SPLIT 2026-06-29] The payment's ORDERED pool of existing HubSpot deal ids. The
  // deals shown here consume it by position (see cellExistingId): the 1–2 visible cards
  // reuse these ids in order, extra cards create new deals, and any pool id left unused
  // is retired (Closed Lost) server-side. This is what makes re-mapping / un-splitting
  // never silently orphan a deal.
  const priorIds = useMemo(
    () => (payment.deals_record_id || '').split(',').map((s) => s.trim()).filter(Boolean),
    [payment.deals_record_id]
  );

  // Build the deal slots from the saved mapping fields. Each slot is a deal type whose
  // four department amounts sum to a positive total; it carries 1 part normally, or its
  // stored 2 parts when it was previously split.
  const initialRows = useMemo(() => {
    const website = payment.website || '';
    const email = payment.email || '';
    const closeDate = payment.payment_date || '';
    const rows: SlotRow[] = [];

    const hasStoredSplits = Object.keys(storedSplits).length > 0;
    // Legacy fallback only: positional ids from the flat deals_record_id (used when no
    // deal_splits layout exists yet — i.e. deals created before this feature).
    const existingIds = payment.deals_record_id
      ? payment.deals_record_id.split(',').map((s) => s.trim()).filter(Boolean)
      : [];

    let idIdx = 0;
    // [DEAL-REUSE 2026-11-03] A stored deal belongs to the FIRST card that holds it (as in assignPriorDeals): a payment
    // saved before the fix with one deal on two slots must not pre-fill the second card from that deal.
    const takenIds = new Set<string>();
    const own = (id?: string): string | undefined => {
      if (!id || takenIds.has(id)) return undefined;
      takenIds.add(id);
      return id;
    };
    for (const dt of DEAL_TYPES) {
      const ranking = Number(savedFields[`ranking_${dt.suffix}`]) || 0;
      const performance = Number(savedFields[`performance_${dt.suffix}`]) || 0;
      const tech = Number(savedFields[`tech_${dt.suffix}`]) || 0;
      const creative = Number(savedFields[`creative_${dt.suffix}`]) || 0;
      // Sum in cents so originalTotal lands on exact cents (the split target).
      const originalTotal = (toCents(ranking) + toCents(performance) + toCents(tech) + toCents(creative)) / 100;

      if (originalTotal > 0) {
        const stored = storedSplits[dt.suffix];
        let parts: [SplitPart, SplitPart];
        let preSplit = false;

        if (stored && stored.length >= 2) {
          // Previously split into two deals — restore both (ids + owners + amounts).
          preSplit = true;
          parts = [
            { ownerName: stored[0]?.owner || '', amountStr: fmtAmountStr(Number(stored[0]?.amount) || 0), existingDealId: own(stored[0]?.dealId), secondVoice: stored[0]?.sv || SELF_CLOSED },
            { ownerName: stored[1]?.owner || '', amountStr: fmtAmountStr(Number(stored[1]?.amount) || 0), existingDealId: own(stored[1]?.dealId), secondVoice: stored[1]?.sv || SELF_CLOSED },
          ];
        } else if (stored && stored.length === 1) {
          // Previously a single deal (created via this flow) — keep its id + owner.
          parts = [
            { ownerName: stored[0]?.owner || payment.pm_name || '', amountStr: fmtAmountStr(originalTotal), existingDealId: own(stored[0]?.dealId), secondVoice: stored[0]?.sv || SELF_CLOSED },
            { ...EMPTY_PART },
          ];
        } else {
          // No stored layout for this slot. Pre-feature row ⇒ positional id; otherwise
          // (a brand-new slot added since the last save) it's a fresh create.
          const existingDealId = hasStoredSplits ? undefined : own(existingIds[idIdx]);
          // An existing deal starts on its stored owner (live read refines it); a brand
          // new deal is pre-filled with the payment's PM when there is one.
          const ownerName = existingDealId ? (storedOwners[idIdx] || '') : (payment.pm_name || '');
          parts = [
            // secondVoice starts blank; for an existing deal the live HubSpot read
            // pre-fills it (see the second-voice refine effect below).
            { ownerName, amountStr: fmtAmountStr(originalTotal), existingDealId, secondVoice: SELF_CLOSED },
            { ...EMPTY_PART },
          ];
        }

        rows.push({
          suffix: dt.suffix,
          dealName: website,
          /* [PIA-DEAL 2026-09-09] Fixed for the four sale/upsell slots; for PIA, derived from the
             saved department tag — which is what makes the owner dropdown offer PM names for a PM
             PIA and Sales names for a Sales one, since renderCard keys that off the pipeline. */
          pipeline: typeof dt.pipeline === 'function' ? dt.pipeline(savedFields) : dt.pipeline,
          paymentType: dt.paymentType,
          closeDate,
          companyUrl: website,
          email,
          ranking,
          performance,
          tech,
          creative,
          originalTotal,
          parts,
          preSplit,
          // [PIA-DEAL 2026-09-09] Only a PIA slot carries this; '' everywhere else.
          piaMonth: dt.suffix === PIA_SUFFIX
            ? piaMonthLabel(payment.month_str, savedFields[PIA_TERM_FIELD])
            : '',
        });
        idIdx++;
      }
    }

    return rows;
  }, [payment, savedFields, storedOwners, storedSplits]);

  const [rows, setRows] = useState<SlotRow[]>(initialRows);
  // Split mode starts ON when the payment already has a split slot, so a re-open lands
  // on the two stored cards (and updates the same deals) rather than collapsing them.
  const [split, setSplit] = useState<boolean>(() => initialRows.some((r) => r.preSplit));
  const [ownersError, setOwnersError] = useState(false);

  // Live state of the existing deal(s), keyed by deal id — drives the "was"
  // (old → new) annotations. Best-effort: an absent id just shows new-only.
  const [existingDeals, setExistingDeals] = useState<Record<string, ExistingHubSpotDeal>>({});
  const [existingError, setExistingError] = useState(false);

  // Parts whose owner the user changed by hand (key = `${rowIdx}:${partIdx}`) — never
  // auto-overwritten by the live-read pre-fill effect below.
  const touchedRef = useRef<Set<string>>(new Set());

  // Latest rows, for effects that must read current rows without re-subscribing to them.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  // Guards the one-shot split reconstruction below so it runs at most once per open.
  const reconciledRef = useRef(false);

  // Fetch HubSpot owners on mount. A failed (or empty) load would leave every
  // Deal Owner dropdown blank and the user hard-blocked by validation, so
  // surface it and offer a retry instead of swallowing the error.
  const loadOwners = React.useCallback(() => {
    setOwnersError(false);
    fetchHubSpotOwners()
      .then((list) => {
        setOwners(list);
        if (!list.length) setOwnersError(true);
      })
      .catch(() => setOwnersError(true));
  }, []);
  useEffect(() => { loadOwners(); }, [loadOwners]);

  // [DEAL-CONFIRM 2026-06-23] Fetch the current state of any existing deal(s) so the
  // popup can show "was X → now Y" (and, for split slots, refine each part's owner).
  // Non-blocking: the user can still confirm while it loads / if it fails.
  useEffect(() => {
    // Read EVERY deal id recorded on the payment (not just the ones assigned to a
    // part), so a split whose layout wasn't stored can still be reconstructed from the
    // live read below — its second deal id lives in deals_record_id, not in any part.
    const partIds = initialRows.flatMap((r) => r.parts.map((p) => p.existingDealId)).filter(Boolean) as string[];
    const recordIds = (payment.deals_record_id || '').split(',').map((s) => s.trim()).filter(Boolean);
    const ids = Array.from(new Set([...partIds, ...recordIds]));
    if (ids.length === 0) return;
    let active = true;
    setExistingError(false);
    fetchHubSpotDeals(ids, payment.id)
      .then((list) => {
        if (!active) return;
        const map: Record<string, ExistingHubSpotDeal> = {};
        for (const d of list) map[d.dealId] = d;
        setExistingDeals(map);
        // A non-empty request that came back empty means we couldn't read them.
        if (list.length === 0) setExistingError(true);
      })
      .catch(() => { if (active) setExistingError(true); });
    return () => { active = false; };
  }, [initialRows, payment.deals_record_id, payment.id]);

  // One filtered+sorted list per context, derived once from the roster.
  const pmOwnerNames = useMemo(
    () => owners.filter((o) => o.role === 'pm' || o.role === 'both').map((o) => o.name).sort((a, b) => a.localeCompare(b)),
    [owners]
  );
  const salesOwnerNames = useMemo(
    () => owners.filter((o) => o.role === 'sales' || o.role === 'both').map((o) => o.name).sort((a, b) => a.localeCompare(b)),
    [owners]
  );

  // When Split turns on, seed any not-yet-split slot with a half/half starting point so
  // the pair opens valid (sums to the total). Only fills empty part-1 amounts, so it
  // never clobbers a stored split or a value the user has already typed.
  useEffect(() => {
    if (!split) return;
    setRows((prev) => prev.map((r) => {
      // [PIA-DEAL 2026-09-09] A PIA belongs to ONE person, so it never splits — leave its
      // second part empty rather than seeding a half it can never use.
      if (isPiaRow(r)) return r;
      if (r.parts[1].amountStr !== '') return r;
      const half = Math.round((r.originalTotal / 2) * 100) / 100;
      const first = Math.round((r.originalTotal - half) * 100) / 100;
      return {
        ...r,
        parts: [
          { ...r.parts[0], amountStr: fmtAmountStr(first) },
          { ...r.parts[1], amountStr: fmtAmountStr(half) },
        ],
      };
    }));
  }, [split]);

  // [DEAL-SPLIT 2026-06-29] Reconstruct a split from the live HubSpot read when the
  // stored layout (deal_splits) is ABSENT — e.g. a deal split before the deal_splits
  // column existed, so deals_record_id holds two ids but nothing says they were a split.
  // A slot whose (pipeline, payment type) matches TWO live deals WAS split, so restore
  // both sides (owner + amount + id, ordered as stored in deals_record_id) and turn
  // Split on. Runs once, and only when there's no stored layout (which stays
  // authoritative — so an intentional un-split via deal_splits is never overridden).
  useEffect(() => {
    if (reconciledRef.current) return;
    if (Object.keys(existingDeals).length === 0) return;
    if (Object.keys(storedSplits).length > 0) return; // stored layout wins
    reconciledRef.current = true;

    const order = (payment.deals_record_id || '').split(',').map((s) => s.trim()).filter(Boolean);
    const idPos = (id?: string) => { const i = id ? order.indexOf(id) : -1; return i < 0 ? Number.MAX_SAFE_INTEGER : i; };

    let anySplit = false;
    const next = rowsRef.current.map((r, ri) => {
      if (touchedRef.current.has(`${ri}:0`) || touchedRef.current.has(`${ri}:1`)) return r;
      // Live deals belonging to THIS slot (each slot is a unique pipeline+paymentType).
      const matches = Object.values(existingDeals).filter(
        (d) => d.pipeline === r.pipeline && d.paymentType === r.paymentType
      );
      if (matches.length < 2) return r;
      const [a, b] = matches.slice().sort((x, y) => idPos(x.dealId) - idPos(y.dealId));
      anySplit = true;
      return {
        ...r,
        preSplit: true,
        parts: [
          { ownerName: a.ownerName || '', amountStr: fmtAmountStr(Number(a.amount) || 0), existingDealId: a.dealId, secondVoice: SELF_CLOSED },
          { ownerName: b.ownerName || '', amountStr: fmtAmountStr(Number(b.amount) || 0), existingDealId: b.dealId, secondVoice: SELF_CLOSED },
        ] as [SplitPart, SplitPart],
      };
    });

    if (anySplit) {
      setRows(next);
      setSplit(true);
    }
  }, [existingDeals, storedSplits, payment.deals_record_id]);

  // Refine an EXISTING deal's owner with its live HubSpot value once the read lands.
  // Untouched parts only — never overwrite a hand-picked owner.
  useEffect(() => {
    if (Object.keys(existingDeals).length === 0) return;
    setRows((prev) => prev.map((r, ri) => {
      const parts = r.parts.map((p, pi) => {
        if (touchedRef.current.has(`${ri}:${pi}`) || !p.existingDealId) return p;
        const ex = existingDeals[p.existingDealId];
        return ex?.ownerName && ex.ownerName !== p.ownerName ? { ...p, ownerName: ex.ownerName } : p;
      }) as [SplitPart, SplitPart];
      return { ...r, parts };
    }));
  }, [existingDeals]);

  // [SECOND-VOICE 2026-08-04] Same idea for Second Voice: pre-fill each existing SALES
  // deal's stored second_voice once the HubSpot read lands, so re-opening the popup shows
  // what is already on the deal instead of a blank the user might overwrite by accident.
  // Untouched parts only — a hand-picked value is never clobbered. Its own 'sv:' touch key.
  useEffect(() => {
    if (Object.keys(existingDeals).length === 0) return;
    setRows((prev) => prev.map((r, ri) => {
      const parts = r.parts.map((p, pi) => {
        if (touchedRef.current.has(`sv:${ri}:${pi}`) || !p.existingDealId) return p;
        const ex = existingDeals[p.existingDealId];
        // [SECOND-VOICE-MULTI 2026-08-06] Normalise both sides: a value typed straight
        // into HubSpot may come back as "A,B", which must not read as a change against
        // our canonical "A, B".
        const live = normSV(ex?.secondVoice || '');
        return live && live !== normSV(p.secondVoice) ? { ...p, secondVoice: live } : p;
      }) as [SplitPart, SplitPart];
      return { ...r, parts };
    }));
  }, [existingDeals]);

  const setOwnerForPart = (rowIdx: number, partIdx: number, name: string) => {
    touchedRef.current.add(`${rowIdx}:${partIdx}`);
    setRows((prev) => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      const parts = r.parts.slice() as [SplitPart, SplitPart];
      parts[partIdx] = { ...parts[partIdx], ownerName: name };
      return { ...r, parts };
    }));
  };

  // [SECOND-VOICE 2026-08-04] Namespaced 'sv:' touch key so picking a second voice
  // doesn't count as touching the OWNER (which would block the owner prefill below),
  // and vice versa.
  // [SECOND-VOICE-MULTI 2026-08-06] Takes the FULL next selection, not one name.
  // 'Self Closed' is EXCLUSIVE — it means the Deal Owner closed it alone, so it cannot
  // sit alongside a named rep. Which side wins is decided by what the user just did:
  // adding a rep while 'Self Closed' was set drops 'Self Closed'; ticking 'Self Closed'
  // over a set of reps clears them. An empty selection is already unreachable (with no
  // clear-all row GlassSelect refuses to untick the last option), but it falls back to
  // 'Self Closed' rather than ever writing a blank.
  const setSecondVoiceForPart = (rowIdx: number, partIdx: number, names: string[]) => {
    touchedRef.current.add(`sv:${rowIdx}:${partIdx}`);
    setRows((prev) => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      const parts = r.parts.slice() as [SplitPart, SplitPart];
      const had = parseSV(parts[partIdx].secondVoice);
      let next = names.filter(Boolean);
      if (next.length > 1 && next.includes(SELF_CLOSED)) {
        next = had.includes(SELF_CLOSED)
          ? next.filter((n) => n !== SELF_CLOSED) // a rep was just added → the rep wins
          : [SELF_CLOSED];                        // 'Self Closed' was just ticked → it wins alone
      }
      if (next.length === 0) next = [SELF_CLOSED];
      parts[partIdx] = { ...parts[partIdx], secondVoice: joinSV(next) };
      return { ...r, parts };
    }));
  };

  // [DEAL-SPLIT-TYPED 2026-10-30, user] A split side is typed as an amount; the other side takes the rest (withSideCents).
  const setSplitAmountForPart = (rowIdx: number, partIdx: number, dollars: number) => {
    setRows((prev) => prev.map((r, i) => (i === rowIdx ? withSideCents(r, partIdx, toCents(dollars)) : r)));
  };

  // [DEAL-SPLIT 2026-06-29] The existing HubSpot deal id each visible card maps to, over the
  // cards in submission order (every row's part 0, then part 1 when split). A card beyond the
  // pool creates a new deal, and any pool id no cell consumes is retired server-side. This is
  // the single source of truth for the Update/New badge AND submit.
  // [DEAL-REUSE 2026-11-03, user] Was BY POSITION, while the server chose BY SLOT: a payment whose
  // one upsell deal was re-mapped to New Sale + Upsell sent BOTH cards to that deal (aciminla.com).
  // Now the one shared rule (utils/dealSlots.ts): a card keeps its own slot's deal, a new deal
  // type reuses a deal no card kept, and one deal never goes to two cards.
  const cellExistingId = useMemo(() => {
    const keys: string[] = [];
    const cards: DealSlotCard[] = [];
    rows.forEach((r, ri) => {
      keys.push(`${ri}:0`); cards.push({ slot: r.suffix, part: 0 });
      if (split) { keys.push(`${ri}:1`); cards.push({ slot: r.suffix, part: 1 }); }
    });
    const ids = assignPriorDeals(cards, storedSplits, priorIds);
    return new Map<string, string | undefined>(keys.map((k, i) => [k, ids[i]]));
  }, [rows, split, priorIds, storedSplits]);

  const handleCreate = async (rowAnswer?: 'ROW' | 'US') => {
    // Every deal that will be sent needs an owner (both parts in split mode).
    const missingOwner = rows.some((r) =>
      split ? (!r.parts[0].ownerName || !r.parts[1].ownerName) : !r.parts[0].ownerName
    );
    if (missingOwner) {
      setError('Please select a name (Deal Owner) for every deal.');
      return;
    }

    // [SECOND-VOICE 2026-08-04] Mandatory on every SALES deal (both halves in split
    // mode), on CREATE and on UPDATE alike — so existing deals get backfilled as they
    // are touched. PM upsell deals are skipped entirely. 'Self Closed' is a valid answer,
    // so this only rejects an actually-empty pick.
    const missingSecondVoice = rows.some((r) => {
      // [PIA-DEAL 2026-09-09] A PIA OFFERS a Second Voice but never requires one — and it never
      // splits, so only its first part could carry one anyway.
      if (isPiaRow(r)) return false;
      if (r.pipeline !== SALES_PIPELINE) return false;
      return split
        ? (!r.parts[0].secondVoice || !r.parts[1].secondVoice)
        : !r.parts[0].secondVoice;
    });
    if (missingSecondVoice) {
      setError(`Every sales deal needs a Second Voice — it defaults to "${SELF_CLOSED}", so this should never be empty.`);
      return;
    }

    // [DEAL-SPLIT 2026-06-29] In split mode each pair MUST add back up to its slot's
    // mapped total — otherwise we'd push more or less money to HubSpot than was mapped.
    // A mismatch shows the top-middle popup and leaves the dialog open until it's fixed.
    if (split) {
      for (const r of rows) {
        // [PIA-DEAL 2026-09-09] PIA is never split, so it has no pair to balance — validating it
        // here would reject the whole submit over an empty second half that is empty by design.
        if (isPiaRow(r)) continue;
        const origC = toCents(r.originalTotal);
        const aC = toCents(r.parts[0].amountStr);
        const bC = toCents(r.parts[1].amountStr);
        if (aC <= 0 || bC <= 0) {
          toastError(`Each split deal must be more than $0.00 — and the two must total ${fmt(r.originalTotal)}.`);
          return;
        }
        if (aC + bC !== origC) {
          const verb = aC + bC > origC ? 'over' : 'under';
          toastError(`Split amounts must total ${fmt(r.originalTotal)} — currently ${fmt((aC + bC) / 100)} (${verb} by ${fmt(Math.abs(aC + bC - origC) / 100)}).`);
          return;
        }
      }
    }

    /* [AMAAN-ROW 2026-09-25, user] A both-books rep (Amaan Sofi, Vishal Suryavanshi) owns a Recurring /
       One-Time New Sale deal on a current-month payment: ask "ROW or US?" NOW, before saving — the answer
       then goes WITH the deal save (one request), so the popup appears at once and there is no second wait.
       The server re-checks every rule.
       [ROW-CLICK-FINAL 2026-10-28, user] "if I click US or if I click ROW, then that will be considered as the
       final tag on that payment" — so it asks even when a Team ROW - Sales owner is on the deal too. The old
       "a Team ROW - Sales owner already decides" check is gone: its regex held two invisible backspace bytes
       where the word boundary was meant, so it never matched and the popup already opened every time — while
       the server dropped the answer once Vishal joined Team ROW - Sales (2026-10-13). */
    if (rowAnswer === undefined) {
      const newSaleOwners = rows
        .filter((r) => r.suffix === 'r_new_sale' || r.suffix === 'o_new_sale')
        .flatMap((r) => (split ? [r.parts[0], r.parts[1]] : [r.parts[0]]))
        .map((pt) => (pt.ownerName || '').trim().toLowerCase());
      const byName = new Map(owners.map((o) => [o.name.trim().toLowerCase(), o]));
      const bothBooks = newSaleOwners.some((n) => !!byName.get(n)?.rowOptional);
      const currentMonth = String(payment.month_str || '').slice(0, 7) >= effectiveCurrentMonth();
      if (bothBooks && currentMonth) {
        setError('');
        setRowAsk({ current: payment.row_status === 'ROW' ? 'ROW' : 'US' });
        return;
      }
    }

    setError('');
    setCreating(true);
    try {
      const buildDeal = (
        r: SlotRow, part: SplitPart, deptCents: number[], partIdx: number, rowIdx: number,
      ): HubSpotDeal => ({
        dealName: r.dealName,
        pipeline: r.pipeline,
        ownerName: part.ownerName,
        paymentType: r.paymentType,
        closeDate: r.closeDate,
        companyUrl: r.companyUrl,
        email: r.email,
        ranking: deptCents[0] / 100,
        performance: deptCents[1] / 100,
        tech: deptCents[2] / 100,
        creative: deptCents[3] / 100,
        // Reuse the prior deal mapped to this card BY POSITION (so a re-map updates the
        // same deals); a card with no prior id creates a new deal.
        existingDealId: cellExistingId.get(`${rowIdx}:${partIdx}`),
        slot: r.suffix,
        part: partIdx,
        // [SECOND-VOICE 2026-08-04] Sales slots only — sending it on a PM upsell would
        // stamp second_voice on a deal that has no such concept. Undefined ⇒ the server
        // leaves the property out of the payload entirely.
        // [PIA-DEAL 2026-09-09] …and PIA slots, whichever pipeline their department tag sent
        // them to: a PIA can be credited to a second voice just like a sale.
        secondVoice: (r.pipeline === SALES_PIPELINE || isPiaRow(r)) ? part.secondVoice : undefined,
        // [PIA-DEAL 2026-09-09] '' on every non-PIA slot ⇒ the server omits the property.
        piaMonth: r.piaMonth || undefined,
      });

      const deals: HubSpotDeal[] = [];
      rows.forEach((r, ri) => {
        // Department order is fixed: [ranking, performance, tech, creative].
        const origDeptCents = [toCents(r.ranking), toCents(r.performance), toCents(r.tech), toCents(r.creative)];
        if (split) {
          const [aDept, bDept] = proportionalSplitCents(origDeptCents, toCents(r.parts[0].amountStr));
          deals.push(buildDeal(r, r.parts[0], aDept, 0, ri));
          deals.push(buildDeal(r, r.parts[1], bDept, 1, ri));
        } else {
          deals.push(buildDeal(r, r.parts[0], origDeptCents, 0, ri));
        }
      });

      const result = await createHubSpotDeals(deals, payment.id, rowAnswer);
      const failed = result.results.filter((r: any) => r.error);
      if (failed.length > 0) {
        setRowAsk(null); // [AMAAN-ROW] back to the deal dialog, where the error shows
        setRowSaving(null);
        setError(`Failed: ${failed.map((f: any) => f.error).join(', ')}`);
      } else {
        // Pass back the deal IDs + owner name(s) + split layout so the parent can update
        // local state (the row's Deal Owner line + the stored split for the next open).
        const newDealIds = result.dealIds?.join(',') || '';
        if (result.rowChoice?.needed) {
          // [AMAAN-ROW 2026-09-25] Fallback only: the answer did not travel with the save (e.g. the
          // owner list lacked the flag), so the server asks now; /row-choice stores the answer.
          setRowSaving(null);
          setRowAsk({
            current: result.rowChoice.current === 'ROW' ? 'ROW' : 'US',
            afterSave: { dealIds: newDealIds, dealOwner: result.dealOwner, dealSplits: result.dealSplits ?? null },
          });
        } else {
          // rowStatus is defined only when the answer was applied with the save → the row's tag updates at once.
          onClose(newDealIds, result.dealOwner, result.dealSplits ?? null, result.rowStatus);
        }
      }
    } catch (err: any) {
      // [DEAL-DEDUP 2026-06-25] A timeout doesn't mean the deal wasn't created — the
      // request may have reached HubSpot. Reassure the user that re-submitting is safe
      // (the server re-uses this payment's saved deal id, so it updates, never duplicates).
      const isTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '');
      setRowAsk(null); // [AMAAN-ROW] back to the deal dialog, where the error shows
      setRowSaving(null);
      setError(isTimeout
        ? 'This is taking longer than expected — the deal may still be processing. You can safely retry; it will update the existing deal, not create a duplicate.'
        : (err.response?.data?.error || err.message || 'Failed to create deals'));
    } finally {
      setCreating(false);
    }
  };

  // "Update" vs "Create" is whether the payment already has ANY deal in its pool.
  const hasExistingDeals = priorIds.length > 0;
  /* [PIA-DEAL 2026-09-09] Split doubles every slot EXCEPT a PIA one, which stays a single card,
     so the count is per-row rather than rows.length * 2. */
  const cardCount = rows.reduce((n, r) => n + (split && !isPiaRow(r) ? 2 : 1), 0);
  const multi = cardCount > 1;
  /* [PIA-DEAL 2026-09-09] There is something to split only when a NON-PIA slot exists. On a
     PIA-only payment the toggle used to still be offered: flipping it widened the modal to the
     side-by-side layout and then showed nothing extra, because a PIA renders as one card either
     way. Hide the control (and keep the narrow width) when nothing can split. */
  const canSplit = rows.some((r) => !isPiaRow(r));

  if (rows.length === 0) {
    onClose();
    return null;
  }

  // Render one deal card (one part of a slot). `editableAmount` ⇒ split mode: the
  // amount becomes an input and the "was" diff annotations are suppressed for clarity.
  const renderCard = (row: SlotRow, rowIdx: number, partIdx: number, editableAmount: boolean) => {
    const part = row.parts[partIdx];
    const isPM = row.pipeline === 'Upsell - Project Managers';
    const list = isPM ? pmOwnerNames : salesOwnerNames;
    // Always keep the selected value present in the list so a pre-filled name (PM /
    // prior owner outside the role list) still displays.
    const displayOwners = part.ownerName && !list.includes(part.ownerName)
      ? [part.ownerName, ...list]
      : list;

    // Update vs New is driven by cellExistingId ([DEAL-REUSE 2026-11-03] the shared rule, no longer by
    // position), so it matches exactly which deal this card will update on save.
    const cellDealId = cellExistingId.get(`${rowIdx}:${partIdx}`);
    const isUpdate = !!cellDealId;
    const old = cellDealId ? existingDeals[cellDealId] : undefined;
    const oldOwner = old?.ownerName || (isUpdate && !editableAmount ? storedOwners[rowIdx] : '') || '';

    const ownerChanged = !editableAmount && isUpdate && !!oldOwner && !!part.ownerName && part.ownerName !== oldOwner;
    // [SECOND-VOICE 2026-08-04] "was X" note when overwriting a stored second voice.
    // [SECOND-VOICE-MULTI 2026-08-06] Compare NORMALISED, so re-saving the same set of
    // names in different spacing isn't reported as a change.
    const secondVoiceChanged = !editableAmount && isUpdate && !!old?.secondVoice
      && !!part.secondVoice && normSV(part.secondVoice) !== normSV(old.secondVoice);
    const pipelineChanged = !editableAmount && isUpdate && !!old && !!old.pipeline && old.pipeline !== row.pipeline;
    const paymentTypeChanged = !editableAmount && isUpdate && !!old && !!old.paymentType && old.paymentType !== row.paymentType;
    const amountChanged = !editableAmount && isUpdate && old?.amount != null && Math.abs(old.amount - row.originalTotal) > 0.005;

    return (
      <DealCard key={`${rowIdx}:${partIdx}`}>
        <DealCardHead>
          <DealName>
            {row.dealName || 'Deal'}
            {isPiaRow(row) && <PiaTag> (PIA)</PiaTag>}
          </DealName>
          <ModeBadge $update={isUpdate}>{isUpdate ? 'Update' : 'New'}</ModeBadge>
        </DealCardHead>

        <FieldRow>
          <FieldLabel>Name</FieldLabel>
          <FieldValue>
            <OwnerWrap>
              <Select liquidList value={part.ownerName} onChange={(e) => setOwnerForPart(rowIdx, partIdx, e.target.value)}>
                <option value="">Select Name</option>
                {displayOwners.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </Select>
            </OwnerWrap>
            {ownerChanged && <WasNote>was {oldOwner}</WasNote>}
          </FieldValue>
        </FieldRow>

        {/* [SECOND-VOICE 2026-08-04] SALES deals only (!isPM ⇒ Sales Pipeline ⇒ the
            r_new_sale / o_new_sale slots). PM upsell deals never show this row and never
            send the property. 'Self Closed' is pinned above the reps: it is what you pick
            when the Deal Owner closed it alone, which is why the owner is NOT filtered out
            of the list below. Mandatory — enforced in handleCreate.
            [PIA-DEAL 2026-09-09] A PIA card also shows it, in EITHER pipeline — a PIA can be
            credited to a second voice like a sale — but there it is OPTIONAL, so handleCreate
            skips PIA rows in the mandatory check. */}
        {(!isPM || isPiaRow(row)) && (
          <FieldRow>
            <FieldLabel>Second Voice</FieldLabel>
            <FieldValue>
              <OwnerWrap>
                <Select liquidList
                  multiple
                  multiUnitLabel="voices"
                  values={parseSV(part.secondVoice)}
                  onChangeValues={(next) => setSecondVoiceForPart(rowIdx, partIdx, next)}
                  title="Click each name that was a second voice · click again to remove"
                >
                  {/* [SECOND-VOICE 2026-08-05] No empty placeholder: every sales deal
                      DEFAULTS to 'Self Closed', so the field is never unset. Change it only
                      when a second rep was actually involved; leaving it alone saves
                      'Self Closed' to HubSpot.
                      [SECOND-VOICE-MULTI 2026-08-06] Omitting the empty option ALSO drives
                      GlassSelect's require-one rule: with no clear-all row it refuses to
                      untick the last remaining name, so this can never reach a blank. */}
                  <option value={SELF_CLOSED}>{SELF_CLOSED}</option>
                  {salesOwnerNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </Select>
              </OwnerWrap>
              {parseSV(part.secondVoice).length > 1 && (
                <SVNames title={part.secondVoice}>{part.secondVoice}</SVNames>
              )}
              {secondVoiceChanged && <WasNote>was {old!.secondVoice}</WasNote>}
            </FieldValue>
          </FieldRow>
        )}

        <FieldRow>
          <FieldLabel>Pipeline</FieldLabel>
          <FieldValue>
            <span>{row.pipeline}</span>
            {pipelineChanged && <WasNote $emphasis>was {old!.pipeline}</WasNote>}
          </FieldValue>
        </FieldRow>

        <FieldRow>
          <FieldLabel>Payment Type</FieldLabel>
          <FieldValue>
            <span>{row.paymentType}</span>
            {paymentTypeChanged && <WasNote>was {old!.paymentType}</WasNote>}
          </FieldValue>
        </FieldRow>

        <FieldRow>
          <FieldLabel>Amount</FieldLabel>
          <FieldValue>
            {editableAmount ? (
              <OwnerWrap>
                <SplitAmountBox
                  label={`Amount of side ${partIdx + 1}`}
                  shown={partAmountText(row, partIdx)}
                  disabled={creating}
                  onType={(v) => setSplitAmountForPart(rowIdx, partIdx, v)}
                />
              </OwnerWrap>
            ) : (
              <>
                <span>{fmt(row.originalTotal)}</span>
                {amountChanged && <WasNote>was {fmt(old!.amount as number)}</WasNote>}
              </>
            )}
          </FieldValue>
        </FieldRow>

        <MetaLine>
          <MetaItem><b>Close date:</b> {fmtDate(row.closeDate)}</MetaItem>
          {row.companyUrl && <MetaItem><b>Company:</b> {row.companyUrl}</MetaItem>}
          {row.email && <MetaItem><b>Email:</b> {row.email}</MetaItem>}
        </MetaLine>
      </DealCard>
    );
  };

  // Portal to <body> so the fixed overlay anchors to the viewport rather than the
  // Payments page container's backdrop-filter (which would centre it mid-page).
  /* [AMAAN-ROW 2026-09-25, user] The "ROW or US?" popup. It opens the moment Create/Update is pressed
     (when a both-books rep owns a New Sale deal), and the answer is saved WITH the deal in one request:
     ROW = counted under Team ROW (ROW tag, ROW row, New Sales ROW bar, EOM ROW - Sales); US = as today.
     It requires an answer — no click-outside close. On a failed deal save it steps back to the deal
     dialog, where the error shows. `afterSave` is the fallback path: the server asked after saving,
     so the answer goes to /row-choice; a refusal (4xx) closes with the server's reason, a network /
     server error keeps the popup so the answer can be retried. */
  if (rowAsk) {
    const answer = async (choice: 'ROW' | 'US') => {
      setRowSaving(choice);
      const saved = rowAsk.afterSave;
      if (!saved) { await handleCreate(choice); return; }
      try {
        const r = await setPaymentRowChoice(payment.id, choice);
        onClose(saved.dealIds, saved.dealOwner, saved.dealSplits, r.rowStatus === 'ROW' ? 'ROW' : null);
      } catch (err: any) {
        const status = Number(err?.response?.status) || 0;
        toastError(err?.response?.data?.error || 'Could not save ROW / US — please try again.');
        if (status >= 400 && status < 500) onClose(saved.dealIds, saved.dealOwner, saved.dealSplits);
        else setRowSaving(null);
      }
    };
    // [FLUID-3 2026-10-12] The same Overlay / Modal stay mounted when the question takes the window over (it only
    // glides narrower); the keys make its three blocks new elements, so they rise in one after another like an
    // opening window instead of swapping in one frame. Inert while the window closes (see the main return).
    return createPortal(
      <Overlay $open={open} inert={closing}>
        <Modal $open={open} style={{ width: 'min(440px, calc(100vw / var(--app-zoom, 1) - 48px))' }}>
          <Header key="row-ask-head">
            <HeaderTitle>ROW or US</HeaderTitle>
          </Header>
          <Body key="row-ask-body">
            <Subhead>This payment will go to the ROW side or the US side?</Subhead>
            <RowAskNote>
              Choose <b>ROW</b> to count this sale under Team ROW, or <b>US</b> to keep it as it is.
              {rowAsk.current === 'ROW' && <> Currently: <b>ROW</b>.</>}
            </RowAskNote>
          </Body>
          <Footer key="row-ask-foot">
            <Btn type="button" disabled={!!rowSaving} onClick={() => answer('US')}>
              <span>{rowSaving === 'US' ? 'Saving…' : 'US'}</span>
            </Btn>
            <Btn type="button" disabled={!!rowSaving} onClick={() => answer('ROW')}>
              <span>{rowSaving === 'ROW' ? 'Saving…' : 'ROW'}</span>
            </Btn>
          </Footer>
        </Modal>
      </Overlay>,
      document.body
    );
  }

  // [FLUID-3 2026-10-12] Inert while it closes: the window stays on screen for the 0.18 s exit, and nothing in it
  // may be used then (a key press must not create the deal a second time).
  return createPortal(
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(undefined); }} $open={open} inert={closing}>
      <Modal $wide={split && canSplit} $open={open}>
        <Header>
          <HeaderRow>
            <HeaderTitle>{hasExistingDeals ? 'Update HubSpot Deal' : 'Create HubSpot Deal'}</HeaderTitle>
            {/* [PIA-DEAL 2026-09-09] Hidden when nothing on this payment can be split — i.e. a
                PIA-only mapping. See canSplit. */}
            {canSplit && (
            <SplitControl>
              <SplitLabel>Split Deals</SplitLabel>
              <Toggle $on={split}>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Split Deals"
                  checked={split}
                  disabled={creating}
                  onChange={() => setSplit((s) => !s)}
                />
              </Toggle>
            </SplitControl>
            )}
          </HeaderRow>
        </Header>

        <Body>
          <SavedNote>Data saved successfully</SavedNote>
          <Subhead>
            {split
              ? `Each deal is split in two — give each side a name and type its amount; the other side fills the rest of the mapped total automatically. ${hasExistingDeals ? 'One updates the existing deal, the other is created.' : 'Both deals will be created.'}`
              : (hasExistingDeals
                  ? `Confirm the changes to ${multi ? 'these deals' : 'this deal'} in HubSpot. Pick the name (Deal Owner) below, then confirm.`
                  : `A HubSpot ${multi ? 'deal will be created for each of these' : 'deal will be created'}. Pick the name (Deal Owner) below, then confirm.`)}
          </Subhead>

          {existingError && (
            <NoticeMsg>
              Couldn't read the current deal values from HubSpot — showing the new values only.
            </NoticeMsg>
          )}

          {rows.map((row, idx) => {
            /* [PIA-DEAL 2026-09-09] A PIA card renders as a single card even with Split Deals on:
               a PIA belongs to one person. Since PIA is last in DEAL_TYPES, this is also what
               puts it BELOW the sale/upsell cards — and it is the only card when PIA is the only
               thing mapped, which is the case for every August 2026 PIA payment. */
            if (!split || isPiaRow(row)) return renderCard(row, idx, 0, false);
            // Split: a side-by-side pair + a live "must total $X" hint.
            const origC = toCents(row.originalTotal);
            const sumC = toCents(row.parts[0].amountStr) + toCents(row.parts[1].amountStr);
            const ok = sumC === origC;
            const diff = (sumC - origC) / 100;
            return (
              <React.Fragment key={idx}>
                <SplitPair>
                  {renderCard(row, idx, 0, true)}
                  {renderCard(row, idx, 1, true)}
                </SplitPair>
                <SplitHint $ok={ok}>
                  {ok
                    ? `Splits total ${fmt(row.originalTotal)} ✓`
                    : `Must total ${fmt(row.originalTotal)} — currently ${fmt(sumC / 100)} (${diff > 0 ? 'over' : 'under'} by ${fmt(Math.abs(diff))})`}
                </SplitHint>
              </React.Fragment>
            );
          })}

          {ownersError && (
            <ErrorMsg>
              Could not load the HubSpot owner list — the Name dropdowns are empty.
              <RetryLink onClick={loadOwners}>Retry</RetryLink>
            </ErrorMsg>
          )}
          {error && <ErrorMsg>{error}</ErrorMsg>}
        </Body>

        <Footer>
          <Btn onClick={() => onClose()}>Cancel</Btn>
          <Btn $variant="primary" onClick={() => handleCreate()} disabled={creating || owners.length === 0}>
            {creating
              ? (hasExistingDeals ? 'Updating...' : 'Creating...')
              : (hasExistingDeals ? (multi ? 'Update Deals' : 'Update Deal') : (multi ? 'Create Deals' : 'Create Deal'))}
          </Btn>
        </Footer>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default DealCreationModal;
